import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Home, Briefcase, Search, User, Wallet, LogOut, MoreVertical, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import HomeScreen from "./screens/HomeScreen";
import MissionsScreen from "./screens/MissionsScreen";
import OffersScreen from "./screens/OffersScreen";
import ProfileScreen from "./screens/ProfileScreen";
import AcompteScreen from "./screens/AcompteScreen";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import PWAInstallButton from "@/components/PWAInstallButton";
import PWAInstallBanner from "@/components/PWAInstallBanner";

const TABS = [
  { to: "/app", label: "Accueil", Icon: Home, exact: true },
  { to: "/app/missions", label: "Missions", Icon: Briefcase },
  { to: "/app/offres", label: "Offres", Icon: Search },
  { to: "/app/acompte", label: "Acompte", Icon: Wallet },
  { to: "/app/profil", label: "Profil", Icon: User },
];

export default function MobileApp() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [confirmLogout, setConfirmLogout] = useState(false);

  // Redirection : si non authentifié → /auth avec next=/app
  useEffect(() => {
    if (!loading && !user) {
      navigate(`/auth?next=${encodeURIComponent(location.pathname)}`, { replace: true });
    }
  }, [loading, user, navigate, location.pathname]);

  // Bloque l'accès admin — l'app mobile est pour les candidats
  if (!loading && user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-8 h-8 border-4 border-[#662483] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const firstName = (user.first_name || user.name?.split(" ")[0] || "candidat").trim();

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col text-base" data-testid="mobile-app-shell">
      {/* Header — simplified, no top-right "Quitter" button (confusing).
          Instead: a discreet ⋮ menu opens with account + logout (with confirmation). */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between" data-testid="mobile-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#662483] to-[#0069B4] flex items-center justify-center text-white text-sm font-black rounded-sm shadow-sm">FR</div>
          <div>
            <p className="text-base font-bold text-[#3F3F3F] leading-tight">FR INTERIM</p>
            <p className="text-xs text-gray-500 mt-0.5">Bonjour, {firstName}</p>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 text-gray-600 hover:text-[#662483] hover:bg-[#F0E9F3] rounded-none"
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-none w-56">
            <DropdownMenuItem
              onClick={() => navigate("/app/profil")}
              className="rounded-none cursor-pointer"
              data-testid="mobile-menu-account"
            >
              <UserCircle className="w-4 h-4 mr-2 text-[#662483]" />
              Mon profil
            </DropdownMenuItem>
            <PWAInstallButton variant="menu" />
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setConfirmLogout(true)}
              className="rounded-none cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid="mobile-menu-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pb-24" data-testid="mobile-content">
        <PWAInstallBanner testId="pwa-install-banner-mobile" />
        <Routes>
          <Route index element={<HomeScreen />} />
          <Route path="missions" element={<MissionsScreen />} />
          <Route path="offres" element={<OffersScreen />} />
          <Route path="acompte" element={<AcompteScreen />} />
          <Route path="profil" element={<ProfileScreen />} />
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </main>

      {/* Bottom tab bar (fixed) — bigger labels + more contrast */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-stretch z-40 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        {TABS.map(({ to, label, Icon, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            className={({ isActive }) =>
              `flex-1 py-2.5 flex flex-col items-center justify-center text-[11px] font-semibold gap-1 transition-colors ${
                isActive ? "text-[#662483]" : "text-gray-600 hover:text-[#3F3F3F]"
              }`
            }
            data-testid={`mobile-tab-${label.toLowerCase()}`}
          >
            {({ isActive }) => (
              <>
                <Icon className={`w-5 h-5 ${isActive ? "text-[#662483]" : ""}`} strokeWidth={isActive ? 2.4 : 1.8} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Logout confirmation dialog — prevents the accidental disconnects reported by the user */}
      <Dialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <DialogContent className="rounded-none max-w-sm" data-testid="logout-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#3F3F3F] flex items-center gap-2">
              <LogOut className="w-5 h-5 text-red-600" /> Confirmer la déconnexion
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 pt-2">
              Vous allez être déconnecté(e) de votre espace FR INTERIM. Vous devrez vous reconnecter pour retrouver vos missions, offres et demandes d&apos;acompte.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
            <Button
              variant="outline"
              onClick={() => setConfirmLogout(false)}
              className="rounded-none flex-1"
              data-testid="logout-cancel-btn"
            >
              Annuler
            </Button>
            <Button
              onClick={() => { setConfirmLogout(false); logout(); }}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white flex-1"
              data-testid="logout-confirm-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
